import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Fetch, API_BASE, authHeaders } from '../utils/Utils.js';
import './EditListItem.css';
import { formatListTitle } from './listCode';

// Back to the list this item was opened from. The page is only reachable from
// a list's items (without that router state it redirects to all lists, below),
// so that list is always the previous history entry; pushing it again would
// leave the edit page behind it for Back to reopen.
const backToItems = (navigate) => navigate(-1);

const EditListItem = () => {
  const { id }       = useParams();
  const navigate     = useNavigate();
  const location     = useLocation();
  const { t }        = useTranslation();

  // Item and list context come from router state set by ListItems row click
  const stateItem     = location.state?.item;
  const stateList     = location.state?.selectedList;

  const [text,       setText]       = useState(stateItem?.text     || '');
  const [category,   setCategory]   = useState(stateItem?.category || '');
  const [status,     setStatus]     = useState(stateItem?.status   || 'pending');
  const [cost,       setCost]       = useState(stateItem?.cost != null ? String(stateItem.cost) : '');
  const [categories, setCategories] = useState([]);
  const [saving,     setSaving]     = useState(false);
  const [msg,        setMsg]        = useState(null); // { type: 'success'|'error', text }
  const [confirmDelete, setConfirmDelete] = useState(false);

  // If no state (direct URL navigation), redirect back
  useEffect(() => {
    if (!stateItem) {
      navigate('/lists/all', { replace: true });
    }
  }, [stateItem, navigate]);

  useEffect(() => {
    Fetch('/api/data/lists.categories', 'GET', null, (data) => setCategories(data || []), () => {});
  }, []);

  const handleSave = (e) => {
    e.preventDefault();
    setSaving(true);
    setMsg(null);
    // Business route: keeps the item's finance transaction in sync when the
    // list is linked to a project (Trello 8mOHyvSN).
    Fetch(
      `/api/lists/items/${id}`,
      'PATCH',
      JSON.stringify({ text: text.trim(), category, status, cost: cost === '' ? null : Number(cost) }),
      () => {
        setSaving(false);
        setMsg({ type: 'success', text: t('lists_item_saved') });
        setTimeout(() => backToItems(navigate), 800);
      },
      () => {
        setSaving(false);
        setMsg({ type: 'error', text: t('lists_item_save_error') });
      }
    );
  };

  const handleDelete = () => {
    fetch(`${API_BASE}/api/lists/items/${id}`, {
      method: 'DELETE',
      headers: authHeaders(),
    }).then(res => {
      if (res.ok) {
        backToItems(navigate);
      } else {
        setMsg({ type: 'error', text: t('lists_item_delete_error') });
        setConfirmDelete(false);
      }
    }).catch(() => {
      setMsg({ type: 'error', text: t('lists_item_delete_error') });
      setConfirmDelete(false);
    });
  };

  if (!stateItem) return null;

  return (
    <div className="edit-item-page">
      <button className="edit-item-back" onClick={() => backToItems(navigate)}>
        ← {t('back')}
      </button>

      {stateList && (
        <div className="edit-item-list-code">{formatListTitle(stateList, t)}</div>
      )}

      <form className="edit-item-form" onSubmit={handleSave}>
        <div className="edit-item-field">
          <label>{t('lists_item')}</label>
          <input
            type="text"
            value={text}
            onChange={e => setText(e.target.value)}
            required
            autoFocus
          />
        </div>

        <div className="edit-item-field">
          <label>{t('category')}</label>
          <select value={category} onChange={e => setCategory(e.target.value)}>
            <option value="">{t('lists_no_category')}</option>
            {categories.map(c => (
              <option key={c._id || c.name} value={c.name}>{c.name}</option>
            ))}
          </select>
        </div>

        <div className="edit-item-field">
          <label>{t('status')}</label>
          <select value={status} onChange={e => setStatus(e.target.value)}>
            <option value="pending">{t('lists_status_pending')}</option>
            <option value="purchased">{t('lists_status_purchased')}</option>
            <option value="completed">{t('lists_status_completed')}</option>
          </select>
        </div>

        <div className="edit-item-field">
          <label>{t('lists_cost')}</label>
          <input
            type="number"
            min="0"
            step="0.01"
            value={cost}
            onChange={e => setCost(e.target.value)}
            data-testid="list-item-cost"
          />
          {stateList?.project_id && status === 'purchased' && !Number(cost) && (
            <div className="edit-item-hint">{t('lists_no_cost_hint')}</div>
          )}
          {status === 'completed' && Number(cost) > 0 && (
            <div className="edit-item-hint">{t('lists_completed_hint')}</div>
          )}
        </div>

        {msg && (
          <div className={`edit-item-msg ${msg.type}`}>{msg.text}</div>
        )}

        <button type="submit" className="edit-item-save" disabled={saving || !text.trim()}>
          {saving ? '…' : t('lists_save')}
        </button>
      </form>

      <hr className="edit-item-divider" />

      {!confirmDelete ? (
        <button
          className="edit-item-delete"
          onClick={() => setConfirmDelete(true)}
        >
          {t('lists_delete_item')}
        </button>
      ) : (
        <button
          className="edit-item-delete-confirm"
          onClick={handleDelete}
        >
          {t('lists_delete_confirm')}
        </button>
      )}
    </div>
  );
};

export default EditListItem;
